import { ADMIN_MESSAGE_FORMATS, isInvoiceResubmissionRequestedFormat, isStageSeatingShareAnnouncementFormat } from '../../constants/messageFormats';
import { appendVariant, buildChatAssetUrl, buildChatAssetUrlWithVariant } from '../../components/common/chat/composer/chatAssetUtils';
import { getGroupNotePreviewText } from '../../components/common/chat/notes/groupNoteMessageUtils';

const API_URL = process.env.REACT_APP_API_URL;
const MESSAGE_IMAGE_PATH = process.env.REACT_APP_MESSAGE_IMAGE_PATH;
const FLYER_IMAGE_PATH = process.env.REACT_APP_FLYER_IMAGE_PATH;
const ADMIN_PROFILE_IMAGE_PATH = process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH;
const GROUP_CHAT_ICON_IMAGE_PATH =
  process.env.REACT_APP_GROUP_CHAT_ICON_IMAGE_PATH || '/api/images/group_chat_icons';
const TACTICART_TARGET_ICON_PATH = '/images/tacticart_logo_whitebackground__proxy-200.jpg';
const TACTICART_PERSONAL_CHAT_TARGET_INTRO =
  'Tacticartの社員やスタッフとの連絡が可能です。@メンション機能でスタッフ名を入力して指定することもできます。';

const buildAssetUrl = (root, file) => buildChatAssetUrl({ apiUrl: API_URL, root, file });
const buildFlyerUrl = (file, variant = 'card') =>
  buildChatAssetUrlWithVariant({ apiUrl: API_URL, root: FLYER_IMAGE_PATH, file, variant });
const buildMessageImageUrl = (file, variant = 'proxy') =>
  buildChatAssetUrlWithVariant({ apiUrl: API_URL, root: MESSAGE_IMAGE_PATH, file, variant });
const buildGroupChatIconUrl = (file, variant = 'proxy') =>
  buildChatAssetUrlWithVariant({ apiUrl: API_URL, root: GROUP_CHAT_ICON_IMAGE_PATH, file, variant });
const buildArtistPersonalNotesListUrl = (artistId, offset = 0, limit = 20) =>
  `${API_URL}/api/artist/personal-chat-notes/${artistId}?offset=${offset}&limit=${limit}`;
const buildArtistMessageFileDownloadUrl = (fileId, apiUrl = API_URL) => {
  const normalizedFileId = String(fileId ?? '').trim();
  if (!normalizedFileId) return '';
  const base = String(apiUrl || '').replace(/\/+$/, '');
  const path = `/api/artist/messages/files/${encodeURIComponent(normalizedFileId)}/download?proxy=stream`;
  return base ? `${base}${path}` : path;
};
const firstNonEmpty = (...vals) => vals.find((v) => typeof v === 'string' && v.trim()) || '';
const normalizeRealtimePayload = (payload = {}) => {
  const source = payload && typeof payload === 'object' ? payload : {};
  const nested = source.message && typeof source.message === 'object' ? source.message : {};
  return { ...source, message: nested };
};
const pickRealtimeThreadId = (payload = {}) => {
  const source = normalizeRealtimePayload(payload);
  return firstNonEmpty(
    source.threadId,
    source.thread_id,
    source.chatThreadId,
    source.chat_thread_id,
    source.groupThreadId,
    source.group_thread_id,
    source.message?.thread_id,
    source.message?.threadId
  );
};
const pickRealtimeCreatedAt = (payload = {}) => {
  const source = normalizeRealtimePayload(payload);
  return firstNonEmpty(source.createdAt, source.created_at, source.message?.created_at) || new Date().toISOString();
};

const buildSidebarPreviewText = (message) => {
  if (!message) return '';
  const fmt = String(message.message_format || '').toLowerCase();
  if (fmt === 'image') return '画像を送信しました';
  if (fmt === 'file') return message.file_original_name || message.file_file_name || 'ファイルを送信しました';
  if (fmt === 'note_create' || fmt === 'note_update' || fmt === 'note_delete') {
    return getGroupNotePreviewText(message);
  }
  if (isInvoiceResubmissionRequestedFormat(fmt)) {
    return '請求書の再発行依頼が届きました。';
  }
  if (isStageSeatingShareAnnouncementFormat(fmt)) {
    return 'ステージシーティングが共有されました。';
  }
  return String(message.content || '').replace(/\s+/g, ' ').trim() || 'メッセージを送信しました';
};

const buildGroupThreadPreviewText = (thread) => {
  const latest = thread?.latest_message || null;
  if (latest) return getGroupNotePreviewText(latest) || buildSidebarPreviewText(latest);
  const preview = String(thread?.latest_message_preview || '').trim();
  if (preview) return preview;
  return '';
};

const buildRealtimeGroupThreadMessage = (payload = {}) => {
  const source = normalizeRealtimePayload(payload);
  const format = String(
    source.messageFormat ||
      source.message_format ||
      source.format ||
      source.message?.message_format ||
      'text'
  ).trim().toLowerCase();
  const createdAt = pickRealtimeCreatedAt(source);
  const preview = firstNonEmpty(
    source.preview,
    source.messagePreview,
    source.message_preview,
    source.content,
    source.text,
    source.message?.content,
    format === 'image' ? '画像を送信しました' : '',
    format === 'file' ? 'ファイルを送信しました' : '',
    '新着メッセージがあります'
  );
  const messageId = firstNonEmpty(source.messageId, source.message_id, source.message?.id, source.message?.message_id);
  return {
    ...source.message,
    id: messageId || `realtime-${createdAt}`,
    message_id: messageId || `realtime-${createdAt}`,
    message_format: format,
    sender_type: String(source.senderType || source.sender_type || source.message?.sender_type || '').trim() || 'system',
    created_at: createdAt,
    content: format === 'text' ? preview : '',
    file_original_name: firstNonEmpty(
      source.fileOriginalName,
      source.file_original_name,
      source.message?.file_original_name,
      source.message?.fileOriginalName
    ),
  };
};

const buildRealtimePersonalTextMessage = (payload = {}) => {
  if (payload?.question) return null;
  const format = String(payload.messageFormat || payload.message_format || 'text').trim().toLowerCase();
  const messageId = String(payload.messageId || payload.message_id || '').trim();
  const content = firstNonEmpty(payload.content, payload.text, payload.preview, payload.messagePreview);
  if (format !== 'text' || !messageId || !content) return null;
  const senderType = String(payload.senderType || payload.sender_type || '').trim().toLowerCase();
  const messageType =
    String(payload.messageType || payload.message_type || '').trim().toLowerCase() ||
    (senderType === 'artist' ? 'artist_message' : 'admin_message');
  const adminUserId =
    payload.senderAdminId ||
    payload.sender_admin_id ||
    payload.adminUserId ||
    payload.admin_user_id ||
    (senderType === 'admin' ? payload.senderId || payload.sender_id : null);
  return {
    id: messageId,
    artist_id: payload.artistId || payload.artist_id || null,
    message_type: messageType,
    message_format: 'text',
    content,
    admin_user_id: messageType === 'admin_message' ? adminUserId || null : null,
    created_at: new Date().toISOString(),
    is_read: false,
    reactions: [],
  };
};

const normalizeId = (value) => (value == null ? '' : String(value));
const isSameId = (left, right) => {
  if (left == null || right == null) return false;
  return normalizeId(left) === normalizeId(right);
};
const pickAdminMentionId = (admin = {}) =>
  normalizeId(
    admin.admin_user_id ||
      admin.adminUserId ||
      admin.admin_id ||
      admin.id ||
      admin.source?.admin_user_id ||
      admin.source?.adminUserId ||
      admin.source?.admin_id ||
      ''
  );
const pickAdminDisplayName = (admin = {}) =>
  firstNonEmpty(
    admin.fullName,
    admin.display_name,
    admin.full_name,
    admin.name,
    admin.admin_name,
    admin.admin_full_name,
    admin.admin_username,
    admin.username,
    admin.admin_nickname,
    admin.nickname,
    admin.source?.admin_name,
    admin.source?.admin_full_name,
    admin.source?.admin_username,
    admin.source?.admin_nickname
  );
const normalizePersonalMentionAdmin = (admin = {}) => {
  const id = pickAdminMentionId(admin);
  const fullName = pickAdminDisplayName(admin);
  if (!id || !fullName) return null;
  const picture = firstNonEmpty(
    admin.picture,
    admin.profile_picture,
    admin.admin_profile_picture,
    admin.pic,
    admin.source?.admin_profile_picture
  );
  const avatarUrl =
    admin.avatarUrl ||
    (picture ? appendVariant(buildAssetUrl(ADMIN_PROFILE_IMAGE_PATH, picture), 'proxy') : '');
  return {
    ...admin,
    id,
    admin_user_id: id,
    fullName,
    display_name: fullName,
    full_name: fullName,
    name: fullName,
    nickname: firstNonEmpty(admin.nickname, admin.admin_nickname, admin.source?.admin_nickname),
    email: firstNonEmpty(admin.email, admin.admin_email, admin.source?.admin_email),
    position: firstNonEmpty(admin.position, admin.admin_position, admin.source?.admin_position),
    department: firstNonEmpty(admin.department, admin.admin_department, admin.source?.admin_department),
    picture,
    profile_picture: picture,
    avatarUrl,
    source: admin.source || admin,
  };
};
const mergePersonalMentionAdmins = (...adminGroups) => {
  const merged = new Map();
  adminGroups.flat().forEach((admin) => {
    const normalized = normalizePersonalMentionAdmin(admin);
    if (!normalized || merged.has(normalized.id)) return;
    merged.set(normalized.id, normalized);
  });
  return Array.from(merged.values());
};
const PERSONAL_ADMIN_PARTICIPANT_LIMIT = 20;
/**
 * @param {{
 *   recentAdmins?: any[],
 *   messageAdmins?: any[],
 *   searchedAdmins?: any[],
 *   limit?: number,
 * }} options
 * @returns {any[]}
 */
const buildPersonalAdminParticipants = ({
  recentAdmins = [],
  messageAdmins = [],
  searchedAdmins = [],
  limit = PERSONAL_ADMIN_PARTICIPANT_LIMIT,
} = {}) => {
  const safeLimit = Math.min(
    Math.max(Number.parseInt(limit, 10) || PERSONAL_ADMIN_PARTICIPANT_LIMIT, 1),
    PERSONAL_ADMIN_PARTICIPANT_LIMIT,
  );
  const recentParticipants = mergePersonalMentionAdmins(recentAdmins, messageAdmins)
    .slice(0, safeLimit);
  return mergePersonalMentionAdmins(recentParticipants, searchedAdmins);
};

const UNSENDABLE_FORMATS = new Set([
  ADMIN_MESSAGE_FORMATS.TEXT,
  ADMIN_MESSAGE_FORMATS.IMAGE,
  ADMIN_MESSAGE_FORMATS.FILE,
]);
const MOBILE_BREAKPOINT_PX = 700;
const ARTIST_TARGET_ROW_CLASSES = {
  root: 'chat-target-row',
  left: 'chat-target-row__media',
  center: 'chat-target-row__text',
  nameLine: 'chat-target-row__title-line',
  name: 'chat-target-row__title',
  preview: 'chat-target-row__preview',
  below: 'chat-target-row__below',
  right: 'chat-target-row__side',
  time: 'chat-target-row__time',
  badge: 'chat-target-row__badge',
};
const getIsMobileViewport = () =>
  typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT_PX : false;

const readQuestionMessageTarget = (search = '') => {
  const params = new URLSearchParams(search || '');
  const threadId = String(params.get('thread') || '').trim();
  const messageId = String(
    params.get('messageId') || params.get('message') || params.get('message_id') || '',
  ).trim();
  return {
    threadId,
    groupAnchorMessageId: threadId ? messageId : '',
    personalAnchorMessageId: threadId ? '' : messageId,
    activeChatTarget: threadId ? 'group' : messageId ? 'personal' : '',
  };
};

export {
  API_URL,
  MESSAGE_IMAGE_PATH,
  FLYER_IMAGE_PATH,
  ADMIN_PROFILE_IMAGE_PATH,
  GROUP_CHAT_ICON_IMAGE_PATH,
  TACTICART_TARGET_ICON_PATH,
  TACTICART_PERSONAL_CHAT_TARGET_INTRO,
  buildAssetUrl,
  buildFlyerUrl,
  buildMessageImageUrl,
  buildGroupChatIconUrl,
  buildArtistPersonalNotesListUrl,
  buildArtistMessageFileDownloadUrl,
  firstNonEmpty,
  normalizeRealtimePayload,
  pickRealtimeThreadId,
  pickRealtimeCreatedAt,
  buildSidebarPreviewText,
  buildGroupThreadPreviewText,
  buildRealtimeGroupThreadMessage,
  buildRealtimePersonalTextMessage,
  normalizeId,
  isSameId,
  pickAdminMentionId,
  pickAdminDisplayName,
  normalizePersonalMentionAdmin,
  mergePersonalMentionAdmins,
  PERSONAL_ADMIN_PARTICIPANT_LIMIT,
  buildPersonalAdminParticipants,
  UNSENDABLE_FORMATS,
  MOBILE_BREAKPOINT_PX,
  ARTIST_TARGET_ROW_CLASSES,
  getIsMobileViewport,
  readQuestionMessageTarget,
};
